import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Alert, Fade,
} from '@mui/material';
import AddIcon       from '@mui/icons-material/AddRounded';
import InventoryIcon from '@mui/icons-material/Inventory2Rounded';
import TableIcon     from '@mui/icons-material/TableChartRounded';
import MedalIcon     from '@mui/icons-material/MilitaryTechRounded';
import StarIcon      from '@mui/icons-material/StarRounded';
import CrownIcon     from '@mui/icons-material/WorkspacePremiumRounded';
import PeopleIcon    from '@mui/icons-material/PeopleAltRounded';
import CheckIcon     from '@mui/icons-material/CheckCircleRounded';
import CancelIcon    from '@mui/icons-material/CancelRounded';
import ToggleOnIcon  from '@mui/icons-material/ToggleOnRounded';
import { useNavigate } from "react-router-dom";

const INITIAL_PACKAGES = [
  { id: 1, name: 'Silver',   price: 999,  isDefault: true,  isOn: true,  isActive: true,  members: 48,  icon: <MedalIcon/>, color: { bg: '#f8fafc', border: '#cbd5e1', text: '#334155', badge: '#e2e8f0', badgeText: '#334155', icon: '#64748b' } },
  { id: 2, name: 'Gold',     price: 1999, isDefault: false, isOn: true,  isActive: true,  members: 67,  icon: <StarIcon/>,  color: { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', badge: '#fde68a', badgeText: '#78350f', icon: '#f59e0b' } },
  { id: 3, name: 'Platinum', price: 4999, isDefault: false, isOn: true,  isActive: false, members: 27,  icon: <CrownIcon/>, color: { bg: '#f5f3ff', border: '#c4b5fd', text: '#4c1d95', badge: '#ddd6fe', badgeText: '#4c1d95', icon: '#7c3aed' } },
];

export default function PackageMaster() {
  const navigate = useNavigate();
  const [packages] = useState(INITIAL_PACKAGES);
  const [alert] = useState(null);

  const stats = {
    total: packages.length,
    active: packages.filter((p) => p.isActive).length,
    enrolled: packages.reduce((a, p) => a + p.members, 0),
  };

  return (
    <Box>
      {alert && (
        <Fade in>
          <Alert severity={alert.type} sx={{ mb: 2, borderRadius: 2, fontSize: '0.8125rem' }}>
            {alert.msg}
          </Alert>
        </Fade>
      )}

      {/* Top Banner Card */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden', mb: 2 }}>
        {/* Gradient header banner */}
        <Box sx={{
          position: 'relative', overflow: 'hidden',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
          color: '#fff', px: 3, py: 2.5,
        }}>
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140,
            borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ position: 'absolute', bottom: -50, right: 80, width: 100, height: 100,
            borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 46, height: 46, borderRadius: 2.5,
                background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifycontent: 'center' }}>
                <InventoryIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>Package Master</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  Manage plans &amp; pricing
                </Typography>
              </Box>
            </Box>

            {/* Stat chips */}
            <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.8,
                borderRadius: 2, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}>
                <InventoryIcon sx={{ fontSize: 18 }} />
                <Box>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.1 }}>{stats.total}</Typography>
                  <Typography sx={{ fontSize: '0.625rem', opacity: 0.85 }}>Total</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.8,
                borderRadius: 2, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}>
                <CheckIcon sx={{ fontSize: 18 }} />
                <Box>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.1 }}>{stats.active}</Typography>
                  <Typography sx={{ fontSize: '0.625rem', opacity: 0.85 }}>Active</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.8,
                borderRadius: 2, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}>
                <PeopleIcon sx={{ fontSize: 18 }} />
                <Box>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.1 }}>{stats.enrolled}</Typography>
                  <Typography sx={{ fontSize: '0.625rem', opacity: 0.85 }}>Enrolled</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Records table Card */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ p: 3 }}>
          
          {/* 🟢 "All Records" आणि "Add Package" बटन आता इथे टेबलच्या वर शिफ्ट केले आहे */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 1.2, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 2,
                background: '#eef2ff', color: '#4338ca',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TableIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>All Records</Typography>
                <Typography variant="caption" color="text.secondary">
                  Complete list of packages
                </Typography>
              </Box>
            </Box>

            <Button variant="contained" size="small" startIcon={<AddIcon />}
              onClick={() => navigate('/dashboard/package-form')}
              sx={{ background: '#6366f1', '&:hover': { background: '#4f46e5' }, borderRadius: 2, px: 2.5, height: 36 }}>
              Add Package
            </Button>
          </Box>

          {/* Table container */}
          <TableContainer sx={{ borderRadius: 2, border: '1px solid #f1f5f9' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f8fafc' }}>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>#</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>Package</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>Price</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>Default</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>Status</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>Visibility</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map((pkg) => (
                  <TableRow key={pkg.id} hover>
                    <TableCell sx={{ fontSize: '0.8125rem', color: '#475569' }}>{pkg.id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ color: pkg.color.icon, fontSize: 18, display: 'flex' }}>{pkg.icon}</Box>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{pkg.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem', color: '#475569' }}>
                      ₹{pkg.price.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={pkg.isDefault
                          ? <StarIcon sx={{ fontSize: '14px !important', color: '#f59e0b !important' }} />
                          : <CancelIcon sx={{ fontSize: '14px !important', color: '#94a3b8 !important' }} />}
                        label={pkg.isDefault ? 'Yes' : 'No'}
                        sx={{
                          fontSize: '0.6875rem', fontWeight: 500, height: 24,
                          background: pkg.isDefault ? '#fffbeb' : '#f1f5f9',
                          color: pkg.isDefault ? '#92400e' : '#475569',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={pkg.isActive
                          ? <CheckIcon sx={{ fontSize: '14px !important', color: '#10b981 !important' }} />
                          : <CancelIcon sx={{ fontSize: '14px !important', color: '#ef4444 !important' }} />}
                        label={pkg.isActive ? 'Active' : 'Inactive'}
                        sx={{
                          fontSize: '0.6875rem', fontWeight: 500, height: 24,
                          background: pkg.isActive ? '#ecfdf5' : '#fef2f2',
                          color: pkg.isActive ? '#065f46' : '#991b1b',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={<ToggleOnIcon sx={{ fontSize: '14px !important',
                          color: `${pkg.isOn ? '#10b981' : '#94a3b8'} !important` }} />}
                        label={pkg.isOn ? 'On' : 'Off'}
                        sx={{
                          fontSize: '0.6875rem', fontWeight: 500, height: 24,
                          background: pkg.isOn ? '#ecfdf5' : '#f1f5f9',
                          color: pkg.isOn ? '#065f46' : '#475569',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}